// uiux-live client scoping + small shared mapper utilities.
//
// Several legacy endpoints REQUIRE an explicit ?client_id= query and return
// 400 without it (GET /api/templates server.js:22096, GET
// /api/company-profiles server.js:21989, GET /api/client-info
// server.js:20936; GET /api/users server.js:19652 needs it for admin
// operators). The legacy app resolves the id from the login payload
// persisted by src/utils/auth.js: localStorage 'userInfo' (.client_id) for
// tenant users, and localStorage 'selectedClientId' for admin/admin_staff
// operators (AppLayout.jsx:275). The backend re-validates every request via
// assertRequestClientAccess, so this is tenant SCOPING, not authorization —
// a wrong or spoofed id is rejected server-side.
//
// When no client scope is resolvable the fetchers short-circuit with an
// 'unavailable' envelope (no network call), degrading ONLY that collection
// (fixtures stay visible) — never the whole app.

import { LiveApiError } from '../liveApiClient';

export const USER_INFO_STORAGE_KEY = 'userInfo';
export const SELECTED_CLIENT_STORAGE_KEY = 'selectedClientId';
const MAX_CLIENT_ID_LENGTH = 128;
const OPERATOR_ROLES = ['admin', 'admin_staff'];

export const isPlainObject = (value) => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const hasControlCharacters = (value) => Array.from(value).some((character) => {
  const code = character.charCodeAt(0);
  return code < 32 || code === 127;
});

// DTO string coercion: strings pass through, finite numbers stringify
// (MySQL ids arrive as numbers), everything else becomes ''.
export const asCleanString = (value) => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
};

// '7月20日'-style label matching the preview fixtures; '' when unparseable.
export const formatMonthDayLabel = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getMonth() + 1}月${date.getDate()}日`;
};

export const resolveStorage = (storage = null) => {
  if (storage) return storage;
  if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  return null;
};

const readStorageItem = (storage, key) => {
  try {
    return storage ? storage.getItem(key) : null;
  } catch (_error) {
    return null;
  }
};

export const readStoredUser = (storage) => {
  const raw = readStorageItem(storage, USER_INFO_STORAGE_KEY);
  if (typeof raw !== 'string' || !raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return isPlainObject(parsed) ? parsed : null;
  } catch (_error) {
    return null;
  }
};

const isUsableClientId = (value) => (
  typeof value === 'string'
  && value.trim().length > 0
  && value.trim().length <= MAX_CLIENT_ID_LENGTH
  && value.trim() !== 'all'
  && !/\s/.test(value.trim())
  && !hasControlCharacters(value)
);

// Accepts an injected storage (tests) or falls back to window.localStorage.
export const resolveClientId = (storage = null) => {
  const store = resolveStorage(storage);
  const user = readStoredUser(store);
  if (!user) return null;
  if (OPERATOR_ROLES.includes(user.role)) {
    // Operators browse a chosen tenant; 'all'/none has no single tenant to
    // scope reads to, so scoped collections degrade instead of leaking
    // cross-tenant lists.
    const selected = readStorageItem(store, SELECTED_CLIENT_STORAGE_KEY);
    return isUsableClientId(selected) ? selected.trim() : null;
  }
  const own = asCleanString(user.client_id);
  return isUsableClientId(own) ? own.trim() : null;
};

// Short-circuit envelope returned by client-scoped fetchers when the
// signed-in tenant cannot be resolved: no network call is made and only
// this one collection degrades (same convention as mappers/kpis.js).
export const unresolvedClientEnvelope = (key) => ({
  status: 'unavailable',
  error: new LiveApiError(
    'live-client-unresolved',
    `「${key}」の取得に必要なログイン情報を確認できません。`,
  ),
});

export const withClientId = (path, clientId, extraParams = null) => {
  const params = new URLSearchParams({ client_id: clientId, ...(extraParams || {}) });
  return `${path}?${params.toString()}`;
};
